import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { UserType } from "@/constants";
import { downloadInvoicePdfForUserType } from "@/lib/download-invoice-pdf";
import { getQueryErrorMessage } from "@/lib/utils";
import { FileText, Loader2 } from "lucide-react";
import { useCallback, useState } from "react";

export interface LicenseInvoiceDownloadButtonProps {
  userType: UserType;
  manualTransactionId: string;
  disabled?: boolean;
  /** Same layout as "Download receipt" in payments-card */
  className?: string;
}

export function LicenseInvoiceDownloadButton({
  userType,
  manualTransactionId,
  disabled = false,
  className = "w-full sm:flex-1",
}: LicenseInvoiceDownloadButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setLoading(true);
      try {
        await downloadInvoicePdfForUserType(manualTransactionId, userType);
      } catch (error) {
        toast({
          title: "Could not download invoice",
          description: getQueryErrorMessage(error),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    },
    [manualTransactionId, userType],
  );

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={(e) => void handleClick(e)}
      disabled={disabled || loading}
      aria-busy={loading}
      className={className}
    >
      {loading ? (
        <>
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          {userType === UserType.REGISTRANT
            ? "Generating Invoice"
            : "Downloading Invoice..."}
        </>
      ) : (
        <>
          <FileText className="h-4 w-4 mr-2" />
          Download Invoice
        </>
      )}
    </Button>
  );
}
